import { type ReactNode, useLayoutEffect, useRef } from 'react';

/**
 * Holds a week-grid cell's height steady while it swaps its static label for
 * the inline autocomplete that edits it.
 *
 * The label wraps (a long customer or activity name flows onto 2+ lines in
 * the fixed-width column), but the editor is a single-line `h-9` input. On a
 * plain swap the cell loses the extra lines, the row collapses (~16px per
 * lost line), and every row below it jumps up — and back down when the
 * editor closes. `min-h-9` on the label only gives the two a common FLOOR;
 * it cannot make a taller label and the editor agree.
 *
 * So the slot remembers the height its static content last rendered at and,
 * for as long as the editor is mounted, uses it as the editor's min-height,
 * with the input centred in that box the way the label's text is. The row
 * keeps its height on open, and closes back to whatever the new label needs.
 *
 * Why measure rather than let the editor wrap: the editor is the shared
 * `Combobox` input used on every screen, and a single-line input is right
 * everywhere else. Changing how it mounts here leaves its internals alone.
 *
 * Mechanics, all in a layout effect so nothing paints in between:
 *  - While NOT editing, measure the slot now and re-measure through a
 *    ResizeObserver, so a label that re-wraps (a name loading in, a window
 *    resize) is current when the user clicks.
 *  - On the switch to editing, the DOM already holds the editor, so the last
 *    measurement is the label's. Apply it as `min-height` before paint.
 *    Cleanup clears it when editing ends.
 *  The observer is disconnected in the same commit that mounts the editor,
 *  so it never records the editor's (shorter) height as the label's.
 */
export function TimesheetWeekGridEditSlot({
  editing,
  children,
}: {
  /** True while the cell's editor (not its static label) is rendered. */
  editing: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const staticHeightRef = useRef(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (editing) {
      const height = staticHeightRef.current;
      // Mounted straight into editing: nothing measured yet, so nothing to hold.
      if (height <= 0) return;
      el.style.minHeight = `${height}px`;
      return () => {
        el.style.minHeight = '';
      };
    }
    const measure = () => {
      staticHeightRef.current = el.getBoundingClientRect().height;
    };
    measure();
    // jsdom (unit tests) has no layout and may lack ResizeObserver; the
    // one-off measure above is all it can offer, and it measures 0 there.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [editing]);

  return (
    <div
      ref={ref}
      data-editing={editing ? '' : undefined}
      // Only while editing: a column flexbox centres the single-line input in
      // the held height. The static label keeps its existing block layout so
      // its own height is exactly what it was before this slot existed.
      className={editing ? 'flex flex-col justify-center' : undefined}
    >
      {children}
    </div>
  );
}
